"use client";

import React, { useState } from "react";

export interface Modality {
  name: string;
  description: string;
}

export interface ModalitiesSectionLabels {
  residentHeading?: string;
  residentSubtitle?: string;
  gradTitle?: string;
  gradBody?: string;
  supervisedTitle?: string;
  supervisedBody?: string;
  affordableTitle?: string;
  affordableBody?: string;
  frameworksHeading?: string;
  frameworksSubtitle?: string;
}

export interface ModalitiesSectionProps {
  modalities: Modality[];
  therapistTier?: 'resident' | 'associate';
  /** Optional label overrides for user-visible strings. */
  labels?: ModalitiesSectionLabels;
}

export function ModalitiesSection({ modalities, therapistTier, labels }: ModalitiesSectionProps) {
  const [selectedModality, setSelectedModality] = useState<number | null>(0);

  const toggleModality = (index: number) => {
    setSelectedModality(selectedModality === index ? null : index);
  };

  // Check if modalities array is empty
  if (!modalities || modalities.length === 0) {
    // Show resident therapist qualifications message if it's a resident
    if (therapistTier === 'resident') {
      return (
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-900">{labels?.residentHeading ?? 'Therapeutic Approach'}</h2>
            <p className="text-sm text-gray-600 mt-1">{labels?.residentSubtitle ?? 'Building expertise through supervised practice'}</p>
          </div>

          <div className="bg-gray-50 rounded-xl p-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                  <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">{labels?.gradTitle ?? 'Graduate-Level Training'}</h3>
                  <p className="text-sm text-gray-600">
                    {labels?.gradBody ?? 'Currently completing advanced clinical training with recent education in evidence-based therapeutic approaches'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                  <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">{labels?.supervisedTitle ?? 'Supervised Practice'}</h3>
                  <p className="text-sm text-gray-600">
                    {labels?.supervisedBody ?? 'Working under close supervision of experienced licensed therapists ensures quality care while developing expertise'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                  <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">{labels?.affordableTitle ?? 'Affordable Care'}</h3>
                  <p className="text-sm text-gray-600">
                    {labels?.affordableBody ?? 'Resident rates make quality therapy more accessible while maintaining professional standards'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // For non-residents with no modalities, return null or a simple message
    return null;
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">{labels?.frameworksHeading ?? 'Therapeutic Frameworks & Approach'}</h2>
        <p className="text-sm text-gray-600 mt-1">{labels?.frameworksSubtitle ?? 'Select a modality to learn more about my approach'}</p>
      </div>
      
      {/* Mobile Layout - Accordion Style */}
      <div className="md:hidden space-y-2">
        {modalities.map((modality, index) => (
          <div key={index}>
            <button
              onClick={() => toggleModality(index)}
              className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-all ${
                selectedModality === index
                  ? 'bg-primary-600 border-2 border-primary-700'
                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
              }`}
            >
              <span className={`font-medium ${
                selectedModality === index ? 'text-white' : 'text-gray-900'
              }`}>
                {modality.name}
              </span>
              <svg 
                className={`w-4 h-4 transition-transform ${
                  selectedModality === index ? 'text-white rotate-90' : 'text-gray-400'
                }`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {selectedModality === index && (
              <div className="mt-2 bg-gray-50 rounded-lg p-4">
                <p className="text-base text-gray-700 leading-relaxed">
                  {modality.description}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop Layout - Two Column */}
      <div className="hidden md:grid md:grid-cols-2 gap-6">
        {/* Left Column - Modalities */}
        <div className="space-y-1">
          {modalities.map((modality, index) => (
            <button
              key={index}
              onClick={() => setSelectedModality(index)}
              className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-all ${
                selectedModality === index
                  ? 'bg-primary-600 border-2 border-primary-700'
                  : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
              }`}
            >
              <span className={`font-medium ${
                selectedModality === index ? 'text-white' : 'text-gray-900'
              }`}>
                {modality.name}
              </span>
              <svg 
                className={`w-4 h-4 ${
                  selectedModality === index ? 'text-white' : 'text-gray-400'
                }`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ))}
        </div>
        
        {/* Right Column - Selected Modality Description */}
        {selectedModality !== null && (
          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-semibold text-lg text-gray-900 mb-4">
              {modalities[selectedModality]?.name}
            </h4>
            <p className="text-base text-gray-700 leading-relaxed">
              {modalities[selectedModality]?.description}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}